import React, { useCallback, useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { MediaStage } from "@/core/types/mediaTypes";
import { ReviewStage } from "@/domains/LibraryView/reviewStage";
import { usePhotoPreloader } from "./usePhotoPreloader";
import { useAppStore } from "@/core/store/useAppStore";
import { ChevronLeftIcon, ChevronRightIcon, ImageIcon, ErrorIcon, SpinnerIcon } from "@/components/icons";
import {
  useReviewKeyboard,
  useBodyScrollLock,
  useReviewQueue,
  LeaderModeIndicator,
  DownloadStatus,
  ReviewSidebar,
  ReviewHeader,
} from "../ReviewModal";

interface PhotoReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  stage?: ReviewStage;
  editorMode?: boolean;
  editorToken?: string;
  initialMediaId?: number;
  /** Reports the current item id as the user navigates, for URL persistence. */
  onCurrentItemChange?: (mediaId: number) => void;
}

export const PhotoReviewModal: React.FC<PhotoReviewModalProps> = ({
  isOpen,
  onClose,
  clientId,
  stage,
  editorMode = false,
  editorToken,
  initialMediaId,
  onCurrentItemChange,
}) => {
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>(null);
  const [addToCollectionStatus, setAddToCollectionStatus] = useState<"adding" | "added" | "no-collection" | null>(null);

  const {
    collections: collectionsState,
    fetchCollections,
    setActiveCollection,
    addItemsToActiveCollection,
    isInActiveCollection,
  } = useAppStore();

  const { collections, activeCollectionId } = collectionsState;

  useEffect(() => {
    if (isOpen && clientId && collectionsState.lastFetchedClientId !== clientId) {
      fetchCollections(clientId);
    }
  }, [isOpen, clientId, collectionsState.lastFetchedClientId, fetchCollections]);

  const {
    items: photos,
    currentItem: currentPhoto,
    currentIndex,
    total,
    isLoading,
    error,
    navigateNext,
    navigatePrev,
    canNavigateNext,
    canNavigatePrev,
    updateItemStage: updatePhotoStage,
    updateItemTags,
    downloadCurrentItem: downloadCurrentPhoto,
  } = useReviewQueue({
    clientId,
    stage,
    contentType: "photos",
    isOpen,
    editorMode,
    editorToken,
    initialMediaId,
  });

  // Report the current item id upward (URL persistence).
  useEffect(() => {
    if (isOpen && currentPhoto) {
      onCurrentItemChange?.(currentPhoto.id);
    }
  }, [isOpen, currentPhoto, onCurrentItemChange]);

  usePhotoPreloader({
    photos,
    currentIndex,
    enabled: isOpen && !isLoading,
  });

  useBodyScrollLock(isOpen);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleDownload = useCallback(async () => {
    try {
      await downloadCurrentPhoto((status) => {
        setDownloadStatus(status);
        if (status === "ready" || status === "failed") {
          setTimeout(() => setDownloadStatus(null), 3000);
        }
      });
    } catch (err) {
      console.error("Download failed:", err);
      setDownloadStatus("failed");
      setTimeout(() => setDownloadStatus(null), 3000);
    }
  }, [downloadCurrentPhoto]);

  const handleStageChange = useCallback(
    async (newStage: MediaStage) => {
      try {
        await updatePhotoStage(newStage);
      } catch (err) {
        console.error("Stage change failed:", err);
      }
    },
    [updatePhotoStage]
  );

  const handleAddToCollection = useCallback(async () => {
    if (!currentPhoto) return;

    if (!activeCollectionId) {
      setAddToCollectionStatus("no-collection");
      setTimeout(() => setAddToCollectionStatus(null), 2000);
      return;
    }

    if (isInActiveCollection(currentPhoto.id)) {
      setAddToCollectionStatus("added");
      setTimeout(() => setAddToCollectionStatus(null), 1500);
      return;
    }

    setAddToCollectionStatus("adding");
    try {
      await addItemsToActiveCollection(clientId, [currentPhoto.id]);
      setAddToCollectionStatus("added");
      setTimeout(() => setAddToCollectionStatus(null), 1500);
    } catch (err) {
      console.error("Failed to add to collection:", err);
      setAddToCollectionStatus(null);
    }
  }, [currentPhoto, activeCollectionId, clientId, addItemsToActiveCollection, isInActiveCollection]);

  const keyboardHandlers = useMemo(
    () => ({
      onNavigateNext: canNavigateNext ? navigateNext : undefined,
      onNavigatePrev: canNavigatePrev ? navigatePrev : undefined,
      onStageChange: !editorMode ? handleStageChange : undefined,
      onDownload: handleDownload,
      onClose: handleClose,
      onAddToCollection: !editorMode ? handleAddToCollection : undefined,
    }),
    [
      canNavigateNext,
      canNavigatePrev,
      navigateNext,
      navigatePrev,
      editorMode,
      handleStageChange,
      handleDownload,
      handleClose,
      handleAddToCollection,
    ]
  );

  const { leaderModeActive } = useReviewKeyboard({
    isOpen,
    handlers: keyboardHandlers,
    canNavigateNext,
    canNavigatePrev,
    editorMode,
  });

  const isCurrentInCollection = currentPhoto ? isInActiveCollection(currentPhoto.id) : false;

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <LeaderModeIndicator isActive={leaderModeActive} />

      <ReviewHeader
        onClose={handleClose}
        title="Photo"
        currentIndex={currentIndex}
        total={total}
        stage={currentPhoto?.stage}
        inPostCount={currentPhoto?.usageCount ?? 0}
      />

      <div className="flex-1 flex min-h-0">
        <div className="flex-1 flex items-center justify-center relative bg-black">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-4">
              <SpinnerIcon className="w-12 h-12 text-cyan-500" />
              <p className="text-gray-400">Loading photos...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-4 text-red-400">
              <ErrorIcon className="w-12 h-12" />
              <p>{error}</p>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-sm text-white transition-colors"
              >
                Close
              </button>
            </div>
          ) : photos.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 text-gray-400">
              <ImageIcon className="w-16 h-16" />
              <p>No photos to review</p>
            </div>
          ) : currentPhoto ? (
            <>
              <img
                key={currentPhoto.id}
                src={currentPhoto.getDisplayUrl()}
                alt={currentPhoto.getName()}
                className="max-w-full max-h-full object-contain"
                draggable={false}
              />

              <button
                onClick={canNavigatePrev ? navigatePrev : undefined}
                disabled={!canNavigatePrev}
                className={`absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full text-white transition-colors ${
                  canNavigatePrev
                    ? "bg-black/60 hover:bg-black/80 cursor-pointer"
                    : "bg-black/30 opacity-30 cursor-not-allowed"
                }`}
              >
                <ChevronLeftIcon className="w-8 h-8" />
              </button>

              <button
                onClick={canNavigateNext ? navigateNext : undefined}
                disabled={!canNavigateNext}
                className={`absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full text-white transition-colors ${
                  canNavigateNext
                    ? "bg-black/60 hover:bg-black/80 cursor-pointer"
                    : "bg-black/30 opacity-30 cursor-not-allowed"
                }`}
              >
                <ChevronRightIcon className="w-8 h-8" />
              </button>
            </>
          ) : null}
        </div>

        <ReviewSidebar
          editorMode={editorMode}
          onStageChange={handleStageChange}
          currentStage={currentPhoto?.stage as MediaStage | undefined}
          collections={collections}
          activeCollectionId={activeCollectionId}
          onCollectionChange={setActiveCollection}
          onAddToCollection={handleAddToCollection}
          addToCollectionStatus={addToCollectionStatus}
          isCurrentInCollection={isCurrentInCollection}
          downloadStatus={downloadStatus}
          onDownload={handleDownload}
          clientId={clientId}
          mediaItem={currentPhoto ?? null}
          onTagsPersisted={updateItemTags}
        />
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
